import { X } from 'lucide-react'

import { cn } from '@/utils/ui/styles'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { useTableSearch } from '../hooks/use-table-search'

type TableSearchProps = {
	placeholder?: string
	paramName?: string
	debounceMs?: number
	analyticsEvent?: string
	className?: string
}

/**
 * Search bar component with URL sync and debounce
 *
 * Features:
 * - Syncs search query with URL params
 * - Debounced input to prevent excessive URL updates
 * - Optional PostHog analytics tracking
 * - Clear button when query is present
 *
 * @example
 * <TableSearch
 *   placeholder="Search for Integration, Name or Description"
 *   analyticsEvent="playbooks_search"
 * />
 */
export function TableSearch({
	placeholder = 'Search for "Name" or "Description"',
	paramName,
	debounceMs,
	analyticsEvent,
	className,
}: TableSearchProps) {
	const { query, setQuery } = useTableSearch({
		paramName,
		debounceMs,
		analyticsEvent,
	})

	return (
		<div
			className={cn(
				'relative flex w-full flex-row items-center justify-start gap-4',
				className,
			)}
		>
			<Input
				type="text"
				name="search"
				onChange={(event) => setQuery(event.target.value)}
				value={query}
				placeholder={placeholder}
				className="h-12 max-w-full"
				tabIndex={0}
			/>
			{query && query.length > 0 && (
				<Button
					size="icon"
					variant="ghost"
					title="Clear search"
					name="clear_search"
					onClick={() => setQuery('')}
					className="absolute inset-y-2 right-2"
				>
					<X />
				</Button>
			)}
		</div>
	)
}
